import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { OrganisationInterface } from 'src/app/model/organisations/organisation-interface';
import { OrganisationType } from 'src/app/model/enums/organisationType';

interface ImportRow {
  data: any;
  organisation: OrganisationInterface;
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'REJECTED' | 'CONFLICT';
  error?: string;
  selected: boolean;
  isConflict?: boolean;
  existingId?: number;
}

@Component({
  selector: 'app-organisation-import-dialog',
  templateUrl: './organisation-import-dialog.component.html',
  styleUrls: ['./organisation-import-dialog.component.css'],
  standalone: false
})
export class OrganisationImportDialogComponent implements OnInit {
  step: 'UPLOAD' | 'MAPPING' | 'REVIEW' = 'UPLOAD';

  csvHeaders: string[] = [];
  csvData: any[] = [];
  delimiter: string = ';'; // Default to semicolon based on user data
  rawCsvText: string = '';

  availableDelimiters = [
    { value: ',', label: 'Komma (,)' },
    { value: ';', label: 'Semikolon (;)' },
    { value: '\t', label: 'Tab' }
  ];

  // Mapping of OrganisationInterface field to CSV header index
  fieldMappings: { [key: string]: string } = {};

  targetFields = [
    { key: 'organisationName', label: 'Name', required: true },
    { key: 'description', label: 'Beschreibung', required: false },
    { key: 'website', label: 'Website', required: false },
    { key: 'street', label: 'Straße', required: false },
    { key: 'city', label: 'Stadt', required: false },
    { key: 'postalCode', label: 'PLZ', required: false },
    { key: 'phoneNumber', label: 'Telefon', required: false }
  ];

  organisationTypes = Object.values(OrganisationType);

  importRows: ImportRow[] = [];
  importedIds: number[] = [];

  isProcessing: boolean = false;
  hasConflicts: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<OrganisationImportDialogComponent>,
    private organisationService: OrganisationService,
    private cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void { }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.rawCsvText = e.target.result;
        this.parseCSV(this.rawCsvText);
      };
      reader.readAsText(file);
    }
  }

  onDelimiterChange(): void {
    if (this.rawCsvText) {
      this.parseCSV(this.rawCsvText, true);
    }
  }

  parseCSV(text: string, manualDelimiter: boolean = false): void {
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) return;

    if (!manualDelimiter) {
      // Detect delimiter if not manually changed
      const firstLine = lines[0];
      const commaCount = (firstLine.match(/,/g) || []).length;
      const semicolonCount = (firstLine.match(/;/g) || []).length;
      const tabCount = (firstLine.match(/\t/g) || []).length;

      this.delimiter = ',';
      if (semicolonCount > commaCount && semicolonCount > tabCount) this.delimiter = ';';
      if (tabCount > commaCount && tabCount > semicolonCount) this.delimiter = '\t';
    }

    this.csvHeaders = this.parseCSVLine(lines[0]);
    this.csvData = lines.slice(1).map(line => this.parseCSVLine(line));

    // Auto-map headers
    this.targetFields.forEach(field => {
      const match = this.csvHeaders.find(h =>
        h.toLowerCase().includes(field.label.toLowerCase()) ||
        h.toLowerCase().includes(field.key.toLowerCase())
      );
      if (match) {
        this.fieldMappings[field.key] = match;
      }
    });

    this.step = 'MAPPING';
    this.cd.detectChanges();
  }

  parseCSVLine(line: string): string[] {
    // Simple CSV parser handling quotes
    const result = [];
    let startValue = 0;
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') inQuotes = !inQuotes;
      if (!inQuotes && line[i] === this.delimiter) {
        result.push(line.substring(startValue, i).replace(/^"|"$/g, '').trim());
        startValue = i + 1;
      }
    }
    result.push(line.substring(startValue).replace(/^"|"$/g, '').trim());
    return result;
  }

  async applyMapping(): Promise<void> {
    this.isProcessing = true;
    this.cd.detectChanges();

    // Fetch all orgs once for conflict detection
    await this.organisationService.getAllOrgs();
    const allOrgs = this.organisationService.sdOrgs || [];

    const rows: ImportRow[] = [];
    for (const dataRow of this.csvData) {
      const org: any = {
        address: {}
      };

      this.targetFields.forEach(field => {
        const header = this.fieldMappings[field.key];
        if (header) {
          const headerIdx = this.csvHeaders.indexOf(header);
          const value = dataRow[headerIdx];

          if (['street', 'city', 'postalCode'].includes(field.key)) {
            org.address[field.key] = value;
          } else {
            org[field.key] = value;
          }
        }
      });

      if (!org.organisationType) {
        org.organisationType = [OrganisationType.DATASPACE_PARTICIPANT];
      } else {
        org.organisationType = this.normalizeOrganisationTypes(org.organisationType);
      }

      // Generate Domain (Required by Backend)
      if (org.organisationName) {
        org.domain = org.organisationName.trim().replace(/\s+/g, '_');
      }

      const row: ImportRow = {
        data: dataRow,
        organisation: org as OrganisationInterface,
        status: 'PENDING',
        selected: true
      };

      // Conflict Detection (Local) - Check both Name and Domain
      const existing = allOrgs.find(o =>
        (o.organisationName?.toLowerCase() === org.organisationName?.toLowerCase()) ||
        (o.domain?.toLowerCase() === org.domain?.toLowerCase())
      );

      if (existing) {
        row.status = 'CONFLICT';
        row.isConflict = true;
        row.existingId = existing.organisationId;
        this.hasConflicts = true;
      }

      rows.push(row);
    }

    this.importRows = rows;
    this.step = 'REVIEW';
    this.isProcessing = false;
    this.cd.detectChanges();
  }

  private normalizeOrganisationTypes(
    organisationType: OrganisationType | OrganisationType[]
  ): OrganisationType[] {
    return (Array.isArray(organisationType)
      ? organisationType
      : [organisationType]
    ).filter(Boolean) as OrganisationType[];
  }

  async acceptRow(row: ImportRow): Promise<void> {
    row.status = 'PENDING';
    row.error = undefined;
    try {
      const result = await this.organisationService.createOrganisation(row.organisation);
      row.status = 'SUCCESS';
      if (result && result.organisationId) {
        this.importedIds.push(result.organisationId);
      }
    } catch (e) {
      row.status = 'FAILED';
      row.error = e.message || 'Error creating organisation';
    }
    this.cd.detectChanges();
  }

  async overwriteRow(row: ImportRow): Promise<void> {
    row.status = 'PENDING';
    row.error = undefined;
    try {
      // Ensure we have the ID for update
      const orgToUpdate = { ...row.organisation, organisationId: row.existingId };
      await this.organisationService.updateOrganisation(orgToUpdate);
      row.status = 'SUCCESS';
    } catch (e) {
      row.status = 'FAILED';
      row.error = e.message || 'Error updating organisation';
    }
    this.cd.detectChanges();
  }

  skipRow(row: ImportRow): void {
    row.status = 'REJECTED';
    this.cd.detectChanges();
  }

  async acceptAll(): Promise<void> {
    this.isProcessing = true;
    for (const row of this.importRows) {
      if (row.status === 'PENDING' || row.status === 'FAILED') {
        await this.acceptRow(row);
      } else if (row.status === 'CONFLICT') {
        // By default don't overwrite on "Accept All" unless specified? 
        // Better to let them use bulk resolve for conflicts.
      }
    }
    this.isProcessing = false;
    this.cd.detectChanges();
  }

  async acceptSelected(): Promise<void> {
    this.isProcessing = true;
    for (const row of this.importRows) {
      if (row.selected) {
        if (row.status === 'PENDING' || row.status === 'FAILED') {
          await this.acceptRow(row);
        } else if (row.status === 'CONFLICT') {
          // Should we overwrite selected conflicts?
        }
      }
    }
    this.isProcessing = false;
    this.cd.detectChanges();
  }

  async overwriteAllConflicts(): Promise<void> {
    this.isProcessing = true;
    for (const row of this.importRows) {
      if (row.status === 'CONFLICT') {
        await this.overwriteRow(row);
      }
    }
    this.isProcessing = false;
    this.cd.detectChanges();
  }

  skipAllConflicts(): void {
    this.importRows.forEach(row => {
      if (row.status === 'CONFLICT') row.status = 'REJECTED';
    });
    this.cd.detectChanges();
  }

  async undoImport(): Promise<void> {
    if (this.importedIds.length === 0) return;
    this.isProcessing = true;
    const idsToUndo = [...this.importedIds];
    for (const id of idsToUndo) {
      try {
        await this.organisationService.deleteItem({ organisationId: id } as OrganisationInterface);
        this.importedIds = this.importedIds.filter(i => i !== id);
        // Update status in table if found
        const row = this.importRows.find(r => r.existingId === id || (r.status === 'SUCCESS' && r.organisation.organisationId === id));
        if (row) row.status = 'REJECTED';
      } catch (e) {
        console.error('Failed to undo import for ID', id, e);
      }
    }
    this.isProcessing = false;
    this.cd.detectChanges();
  }

  rejectAll(): void {
    this.importRows.forEach(row => {
      if (row.status === 'PENDING') row.status = 'REJECTED';
    });
    this.cd.detectChanges();
  }

  toggleAll(event: any): void {
    const isChecked = event.checked;
    this.importRows.forEach(row => row.selected = isChecked);
    this.cd.detectChanges();
  }

  onClose(): void {
    this.dialogRef.close(true);
  }
}
